/** tm/legal/legalTypes.ts — 법령·판례·뉴스 API 타입 (/api/tm/legal) — 계약이 바뀌면 이 파일만 고친다 */

export type FeedTab = 'all' | 'law' | 'prec' | 'expc' | 'bill' | 'notice' | 'cabinet' | 'news';
export type AlertTab = 'law' | 'prec' | 'expc' | 'bill' | 'notice' | 'cabinet';
/** 법령 종류 필터 (시행령 = 대통령령 · 시행규칙 = 총리령·부령) */
export type LawKind = '법률' | '시행령' | '시행규칙';
export type ItemTab = Exclude<FeedTab, 'all'>;
export type LegalGroup = '법령' | '판례' | '심판례' | '유권해석';
export type RunStatus = 'running' | 'ok' | 'partial' | 'error';

// ── 주제 ──
export interface TopicPin {
  law: string; jo: string; title: string; url: string;
  upcoming: { effective_at: string; kind: string } | null;
}
export interface Subtopic { label: string; pattern: string; count?: number }
export interface Topic {
  id: number; label: string; description: string | null;
  pins: TopicPin[]; subtopics: Subtopic[];
  counts: { all: number; law: number; prec: number; expc: number; bill?: number; notice?: number; cabinet?: number; news: number };
  color?: string | null;
  alerts: number; newsNew: number;
  /** 새 글 알림 여부 (false 면 벨·배지 대신 회색 NEW 수만) — 없으면 true */
  alert?: boolean;
  feed?: TopicFeed | null;
  newCount?: number;
  alertsByTab?: Partial<Record<AlertTab, number>>;
  subCounts?: Record<string, number>;
}

export interface TopicFeed { type: 'tax_prec' | 'all_laws' | string }

// ── 피드 ──
export interface LawMeta {
  promulgationNo?: string; status?: string; mst?: string; efYd?: string; effectiveDates?: string[];
  lawType?: string; minor?: boolean; compare_url?: string | null; revision_url?: string | null;
}
interface FeedBase {
  id: number; tab: ItemTab; title: string; sort_date: string | null; first_seen_at: string;
  is_new: boolean; unread: boolean; starred: boolean; topics: string[]; public_url?: string | null;
}
export interface LegalFeedRow extends FeedBase {
  kind: 'legal';
  source: string; source_label: string; group: LegalGroup | null;
  case_no: string | null; org: string | null; kind_label: string | null;
  decided_at: string | null; effective_at: string | null; law_kind?: string | null;
  meta: LawMeta | null; law_refs: string[]; summary: string | null; company_count: number;
  /** 같은 내용(같은 회신) 묶음: 대표 행에 나머지 id · 건수, 날짜 의심이면 date_note */
  dup_ids?: number[]; dup_count?: number; date_note?: string | null;
  /** 내 제목 (본인만 · null = 원래 제목) */
  custom_title?: string | null;
}
export interface NewsFeedRow extends FeedBase {
  kind: 'news';
  lead_source: string; lead_url: string; article_count: number; last_published_at: string;
}
export type BillStatus = '계류' | '위원회 심사' | '본회의 부의' | '가결' | '부결' | '폐기' | '대안반영폐기' | '철회' | string;
export interface BillFeedRow extends FeedBase {
  kind: 'bill';
  bill_no: string | null; proposer: string | null; propose_dt: string | null; committee: string | null;
  status: BillStatus | null; detail_url: string | null;
  lead_source: string | null; lead_url: string | null; last_published_at: string | null;
}
export interface NoticeFeedRow extends FeedBase {
  kind: 'notice';
  ministry: string | null; law_kind: string | null; start_date: string | null; end_date: string | null;
  summary: string | null; days_left: number | null; lead_source: string | null; lead_url: string | null;
}
/** 국무회의 의결 (정부입법현황) — status = 현재 단계 (국무회의 · 공포대기 · 국회제출 · 공포) */
export interface CabinetFeedRow extends FeedBase {
  kind: 'cabinet';
  ministry: string | null; law_kind: string | null; amend_kind: string | null; status: string | null;
  cabinet_date: string | null; cabinet_no: number | null; promulgated_date: string | null; promulgation_no: string | null;
  assembly_bill_no: string | null; submitted_date: string | null; summary: string | null;
  lead_source: string | null; lead_url: string | null;
}
export type FeedRow = LegalFeedRow | NewsFeedRow | BillFeedRow | NoticeFeedRow | CabinetFeedRow;
/** facets = 지금 조건(주제·검색어·세부 주제·새 소식·★)에서 구분별 건수 (법령 종류를 고르면 null) */
export type FeedFacets = Partial<Record<FeedTab, number>>;
export interface FeedPage { rows: FeedRow[]; total: number; page: number; pageSize: number; facets?: FeedFacets | null }
export interface FeedQuery {
  topic: string; tab: FeedTab; sub: string; q: string; onlyNew: boolean;
  starred: boolean;
  lawKind: LawKind | '';
}

// ── 상세 ──
export interface LinkedCompany { company_id: string; name: string; note: string | null; linked_at: string }
export interface LegalItemDetail {
  id: number; source: string; source_label: string; group: LegalGroup | null; tab: ItemTab | null;
  title: string; case_no: string | null; org: string | null; requester: string | null;
  kind: string | null; kind_label?: string | null;
  decided_at: string | null; effective_at: string | null; public_url: string | null;
  meta: LawMeta | null; keywords: string[]; topics?: string[]; law_refs: string[];
  summary: string | null; detail: Record<string, string> | null; detail_status: string;
  /** 요지가 없을 때 이유 ('목록만 제공' · '원문에서 확인') */
  detail_note?: string | null; date_note?: string | null;
  siblings?: { id: number; case_no: string | null; title: string; source: string; decided_at: string | null; public_url: string | null; lead: boolean }[];
  read_at: string | null; starred: boolean; memo: string; companies: LinkedCompany[];
  /** 내 제목 (본인만 · null = 원래 제목) */
  custom_title?: string | null;
  /** 내 메모 첨부 — 서버가 표를 아직 만들지 않았으면 null/없음 → 화면이 따로 불러온다 */
  files?: LegalMemoFile[] | null;
}
/** 「내 메모」 첨부 파일 (본인만) */
export interface LegalMemoFile {
  id: string; item_id: number; file_name: string; size: number; mime: string; created_at: string;
}
export interface NewsArticle {
  id?: number; url: string; title: string; source: string; published_at: string;
  provider: string; snippet: string | null;
}
export interface NewsDetail {
  id: number; title: string; lead_source: string; lead_url: string; article_count: number;
  first_published_at: string; last_published_at: string; topics: string[];
  starred: boolean; articles: NewsArticle[];
}
export interface BillDetail {
  id: number; bill_id: string; bill_no: string | null; title: string; proposer: string | null; propose_dt: string | null;
  committee: string | null; status: BillStatus | null; detail_url: string | null; topics: string[];
  first_seen_at: string; status_changed_at: string | null; is_new: boolean; unread: boolean; starred: boolean; read_at?: string | null;
  /** 제안이유·주요내용 (없으면 의안 상세에서) */
  summary?: string | null;
}
export interface NoticeDetail {
  id: number; notice_id?: string; title: string; ministry: string | null; law_kind: string | null;
  start_date: string | null; end_date: string | null; days_left?: number | null; detail_url: string | null;
  summary: string | null; topics: string[]; law_refs?: string[]; first_seen_at: string; starred: boolean; read_at?: string | null;
}
/** 정부입법현황 단계 한 줄 (입법예고 · 법제처심사완료 · 차관회의 · 국무회의 · 국회제출 · 공포대기 · 공포) */
export interface GovStage { stage: string; date: string | null; end_date?: string | null; no?: number | null; prom_no?: string | null; bill_no?: string | null; text?: string }
export interface CabinetDetail {
  id: number; lbic_id: string; title: string; ministry: string | null; law_kind: string | null; amend_kind: string | null; status: string | null;
  cabinet_date: string | null; cabinet_no: number | null; notice_start: string | null; notice_end: string | null;
  submitted_date: string | null; assembly_bill_no: string | null; promulgated_date: string | null; promulgation_no: string | null;
  detail_url: string | null; topics: string[]; law_refs?: string[]; summary: string | null; stages: GovStage[];
  first_seen_at: string; is_new: boolean; unread: boolean; starred: boolean; read_at?: string | null;
}
/** 같은 법령안의 진행 단계 (GET /cabinet/progress) — link = 그 단계의 글 (입법예고·국무회의·국회 법안·법령 개정) */
export interface ProgressStep {
  stage: string; label: string; date: string | null; end_date: string | null; no: number | null; prom_no: string | null; bill_no: string | null;
  note?: string | null; link: { kind: ItemKind; id: number } | null;
}
export interface GovProgress {
  gov: { id: number; title: string; status: string | null; law_kind: string | null; ministry: string | null; detail_url: string | null; in_feed: boolean };
  steps: ProgressStep[];
}
export interface BillsStatus { configured: boolean; age?: number; lastRun: { finished_at: string | null; status: RunStatus } | null; running: boolean }
export type ItemKind = FeedRow['kind'];
export type Selection = { kind: ItemKind; id: number } | null;

// ── 알림 ──
export interface AlertItem {
  id: number; title: string; source_label: string; topic: string;
  first_seen_at: string; kind_label: string | null; minor?: boolean;
  /** 내 제목 (법령·판례·해석만) */
  custom_title?: string | null;
  /** 한 줄 미끼 문구 (요지 앞부분 · 시행 D-12 · 상태 변화) */
  hook?: string | null;
}
export type AlertKey = 'law' | 'legal' | 'bill' | 'notice' | 'cabinet';
export interface AlertGroup { key: AlertKey; label: string; count?: number; items: AlertItem[] }
/** top = 서버가 중요도 순으로 고른 상위 항목 (group = 묶음 key) */
export type AlertTop = AlertItem & { group: AlertKey; score?: number };
export interface LegalAlerts { total: number; groups: AlertGroup[]; newsToday: number; latest_first_seen_at?: string | null; top?: AlertTop[] }
/** POST /alerts/read-all → 되돌리기용 id */
export interface ReadAllIds { legal: number[]; bill: number[]; notice: number[]; cabinet: number[] }

// ── 요약(상태 줄) ──
export interface LegalRun {
  status: RunStatus; started_at: string | null; finished_at: string | null;
  new_count: number; error: string | null; auth_error?: boolean; trigger?: string | null;
}
export interface LegalSummary {
  lastRun: LegalRun | null; running: boolean; ocConfigured: boolean; authError: boolean;
  newsProviders?: { google: boolean; naver: boolean };
  news?: { enabled: boolean; lastRun: { finished_at: string | null; status: RunStatus } | null };
  firstRun?: boolean;
  backfill?: { active: boolean; done?: number; total?: number };
  nextRun?: string | null;
  batch?: BatchInfo | null;
}
export interface BatchStep { key: string; label?: string; status: RunStatus | 'pending' | 'skipped' | string; note?: string | null; new_count?: number | null; error?: string | null; ms?: number | null; finished_at?: string | null }
export interface BatchRun { status?: RunStatus | string; trigger?: string; started_at?: string | null; finished_at?: string | null; steps?: BatchStep[]; error?: string | null }
/** /summary.batch = {schedule, nextRun, running, lastRun} */
export interface BatchInfo { schedule?: string; nextRun?: string | null; running?: boolean; lastRun?: BatchRun | null }

export interface NewsStatus {
  lastRun: { finished_at: string | null; started_at?: string | null; status: RunStatus } | null;
  providers: { google: boolean; naver: boolean }; running: boolean; enabled?: boolean;
}

// ── 대시보드 · 업체 ──
export interface DigestItem { id: number; title: string; source_label: string; decided_at: string | null; effective_at?: string | null; kind?: string }
export interface DigestTopic { label: string; count: number; news?: number; items: DigestItem[] }
export interface LegalDigest { date: string; total: number; news?: number; topics: DigestTopic[] }
export interface CompanyLegalItem {
  id: number; title: string; source: string; source_label: string; group: LegalGroup | null;
  case_no: string | null; decided_at: string | null; link_note: string | null; linked_at: string;
  /** 내 메모 · 내 메모 첨부 (본인 것만) */
  memo?: string; files?: LegalMemoFile[]; custom_title?: string | null;
}

// ── 설정 (super) ──
export interface PinInput { law: string; jo: string; title: string }
export interface LegalKeyword {
  id: number; label: string; description: string | null;
  terms: string[]; body_terms: string[]; news_terms: string[]; news_must: string[]; bill_terms: string[];
  news_exclude: string[]; exclude_terms: string[];
  sources: string[] | null; pins: PinInput[]; subtopics: Subtopic[];
  active: boolean; alert: boolean; feed: TopicFeed | null; sort: number;
}
export type KeywordInput = Omit<LegalKeyword, 'id' | 'sort' | 'feed'> & { sort?: number };
/** 저장 시 약어↔정식 명칭 자동 확장 내역 */
export interface TermExpansion { field: string; from: string; to: string[] | string }
export type SavedKeyword = LegalKeyword & { expanded?: TermExpansion[] };
export interface WatchedLaw { id: number; name: string; active: boolean; sort: number }
export interface LegalSettings {
  sms: { enabled: boolean; phones: string[]; minCount: number };
  alertOtherLawAmend: boolean; newsEnabled: boolean; lastSmsDate?: string | null;
}
export interface SmsResult { phone: string; ok: boolean; error: string | null }
export interface TestSmsResult { total: number; message: string; results: SmsResult[] }
export interface LegalSource { key: string; label: string; group: LegalGroup; tab?: string; hasDetail: boolean; enabled: boolean }
/** 동의어 묶음 — 각 배열은 서로 같은 뜻 */
export type SynonymGroups = string[][];
/** 동의어 + 약칭 목록 (약칭은 법령 제목 검색에 권하지 않음) */
export interface SynonymData { groups: SynonymGroups; abbr: string[] }
/** 「곧 시행」 한 줄 — 같은 법령·같은 시행일은 공포 여러 건을 묶음 */
export interface UpcomingRow { law: string; effective_at: string; d_day?: number | null; kind?: string | null; promulgations: { no: string | null; date: string | null; id: number }[] }

/** 다른 화면에서 법령·판례·뉴스로 이동할 때 — alerts: 새 소식 패널을 연 채로 */
export interface LegalNavParams { topic?: string; itemId?: number; kind?: ItemKind; alerts?: boolean }
/** 주제별 자동 수집 설명 (읽기 전용 배지) */
export const FEED_LABEL: Record<string, string> = { tax_prec: '세법 판례: 국세·지방세 판례 전체 자동 수집', all_laws: '최신 법령 전체: 모든 법령 개정' };
